import { useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';

import { SInfiniteScroll } from '@setvi/shared/components';
import type { Product, ProductAssetDocument } from '@setvi/shared/interfaces';
import {
  AssetPreview,
  isWebPageAsset,
  openProductAssetFile
} from 'componentsV2/asset-preview';
import { ProductsEmptyState } from 'componentsV2/product/details/content/components';
import {
  toResourceRow,
  useProductAssets
} from 'componentsV2/product/details/content/subpages/associated-resource/product-assets';

import {
  MobileResourceCard,
  MobileResourceLoader,
  MobileResourceOverlay
} from '../components';
import { TabContentWrapper } from '../../styled';

const useStyles = makeStyles(({ spacing }) => ({
  wrapper: {
    marginTop: spacing(2)
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    padding: spacing(0, 2, 2)
  }
}));

const keyGetter = (document: ProductAssetDocument) => document.id;

interface MobileProductAssetsProps {
  product: Product;
}

/**
 * Mobile Resources tab for product-assets companies (CFS): the same cards and
 * the same full-screen overlay (back button) as Setvi resources, with the
 * product-assets preview inside instead of the viewer iframe.
 */
export const MobileProductAssets = ({ product }: MobileProductAssetsProps) => {
  const [selected, setSelected] = useState<ProductAssetDocument | null>(null);
  const { documents, isLoading } = useProductAssets({ product });
  const classes = useStyles();

  // Every file opens in the overlay (which says so for what it cannot
  // render); only a web page opens as itself.
  const open = (document: ProductAssetDocument) => {
    if (isWebPageAsset(document)) openProductAssetFile(document.url);
    else setSelected(document);
  };

  const content = () => {
    if (selected)
      return (
        <MobileResourceOverlay
          title={selected.name}
          onBack={() => setSelected(null)}>
          <Box className={classes.preview}>
            <AssetPreview asset={selected} />
          </Box>
        </MobileResourceOverlay>
      );

    if (isLoading) return <MobileResourceLoader />;

    if (!documents.length)
      return <ProductsEmptyState title="No Resources Available" />;

    return (
      <SInfiniteScroll
        list={documents}
        keyGetter={keyGetter}
        wrapperClass={classes.wrapper}
        onWrapperClick={open}
        render={(document: ProductAssetDocument) => (
          <MobileResourceCard resource={toResourceRow(document)} />
        )}
      />
    );
  };

  return <TabContentWrapper>{content()}</TabContentWrapper>;
};
